import React, { useState, useEffect } from 'react';
import { Plus, Trash2, FileText, CheckCircle, Search } from 'lucide-react';
import InvoicePreview from './InvoicePreview';

const STANDARD_PRODUCTS = [
  { description: 'Software Development Services', hsnSacCode: '998313', netPrice: 75000, gstPercentage: 18 },
  { description: 'IT Consulting Services', hsnSacCode: '998311', netPrice: 50000, gstPercentage: 18 },
  { description: 'Server Setup and Deployment', hsnSacCode: '998715', netPrice: 25000, gstPercentage: 18 },
  { description: 'Cloud Infrastructure Hosting', hsnSacCode: '998422', netPrice: 15000, gstPercentage: 18 },
  { description: 'Digital Marketing & SEO Campaign', hsnSacCode: '998315', netPrice: 35000, gstPercentage: 18 },
  { description: 'Technical Support (Annual Maintenance)', hsnSacCode: '998716', netPrice: 45000, gstPercentage: 18 }
];

const PRESETS = [
  { name: 'Indigo', value: '#4F46E5' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Slate', value: '#475569' },
  { name: 'Rose', value: '#F43F5E' }
];

const InvoiceForm = ({ user, templates, onCreated }) => {
  const [biller, setBiller] = useState({
    companyName: user?.companyName || user?.CompanyName || 'Intechronix',
    address: '119 vistar nagar bargadwa\nGorakhpur District, Uttar Pradesh 273007',
    contact: '',
    gstin: '09JAHPK7767J1ZS'
  });

  const [billTo, setBillTo] = useState({ company: '', name: '', address: '', gstin: '' });
  const [items, setItems] = useState([{ description: '', hsnSacCode: '', qty: 1, netPrice: 0, gstPercentage: 18, saveToCatalog: false }]);
  const [standardProducts, setStandardProducts] = useState([]);
  const [templateColor, setTemplateColor] = useState('#4F46E5');
  
  useEffect(() => {
    fetchCatalogProducts();
  }, []);

  const fetchCatalogProducts = async () => {
    try {
      const res = await fetch('/api/invoice/products');
      if (res.ok) {
        const data = await res.json();
        setStandardProducts(data);
      }
    } catch (err) {
      console.error("Could not fetch standard products", err);
    }
  };

  const handleQuickAdd = (e) => {
    const prodIdx = e.target.value;
    if (prodIdx === "") return;
    const finalProducts = standardProducts.length > 0 ? standardProducts : STANDARD_PRODUCTS;
    const selected = finalProducts[prodIdx];
    if (items.length === 1 && items[0].description === '' && items[0].netPrice === 0) {
      setItems([{ ...selected, qty: 1, saveToCatalog: false }]);
    } else {
      setItems([...items, { ...selected, qty: 1, saveToCatalog: false }]);
    }
    e.target.value = ""; // Reset dropdown
  };
  
  const [payment, setPayment] = useState({
    transactionDesc: 'Payment for Services',
    transactionDate: new Date().toISOString().split('T')[0],
    modeOfPayment: 'Bank Transfer',
    transactionId: '',
    amount: 0
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState(user?.preferredTemplateId || user?.PreferredTemplateId || 1);
  const [invoicePrefix, setInvoicePrefix] = useState('#INT-IV');
  const [paymentStatus, setPaymentStatus] = useState('Unpaid');
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [selectedTemplateComponent, setSelectedTemplateComponent] = useState('TemplateModern');

  useEffect(() => {
    if (templates && templates.length > 0) {
      const template = templates.find(t => t.id == selectedTemplateId) || templates[0];
      setSelectedTemplateComponent(template.componentName);
    }
  }, [selectedTemplateId, templates]);

  const [isVerifying, setIsVerifying] = useState({ biller: false, billTo: false });

  const handleGSTVerify = async (section) => {
    const gstin = section === 'biller' ? biller.gstin : billTo.gstin;
    if (!gstin || gstin.length < 15) {
      alert("Please enter a valid 15-digit GSTIN first.");
      return;
    }

    setIsVerifying({ ...isVerifying, [section]: true });
    try {
      const response = await fetch('/api/gst/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          gstin: gstin
        })
      });

      const data = await response.json();
      if (data.valid && data.company_details) {
        const details = data.company_details;
        const address = details.pradr?.addr || "";
        const tradeName = details.trade_name || details.legal_name || "";
        const legalName = details.legal_name || "";

        if (section === 'biller') {
          setBiller({ ...biller, companyName: tradeName, address: address });
        } else {
          setBillTo({ ...billTo, company: tradeName, address: address, name: legalName });
        }
      } else {
        alert("Invalid GSTIN or no details found.");
      }
    } catch (err) {
      console.error(err);
      alert("Error verifying GSTIN. Check connection or CORS.");
    } finally {
      setIsVerifying({ ...isVerifying, [section]: false });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { description: '', hsnSacCode: '', qty: 1, netPrice: 0, gstPercentage: 18, saveToCatalog: false }]);
  const removeItem = (index) => items.length > 1 && setItems(items.filter((_, i) => i !== index));

  const calculateItemTotals = (item) => {
    const netTotal = parseFloat(item.qty) * parseFloat(item.netPrice || 0);
    const gstRate = parseFloat(item.gstPercentage || 0);
    const cgstAmount = (netTotal * (gstRate / 2)) / 100;
    const sgstAmount = (netTotal * (gstRate / 2)) / 100;
    return { netTotal, cgstAmount, sgstAmount, totalItemPrice: netTotal + cgstAmount + sgstAmount };
  };

  let subTotal = 0, totalCGST = 0, totalSGST = 0, grandTotal = 0;
  items.forEach(item => {
    const totals = calculateItemTotals(item);
    subTotal += totals.netTotal;
    totalCGST += totals.cgstAmount;
    totalSGST += totals.sgstAmount;
    grandTotal += totals.totalItemPrice;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Save items marked as "Save to Catalog" to DB
    const saveCatalogPromises = items
      .filter(item => item.saveToCatalog && item.description.trim() !== '')
      .map(item => {
        return fetch('/api/invoice/products', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            description: item.description,
            hsnSacCode: item.hsnSacCode,
            netPrice: parseFloat(item.netPrice || 0),
            gstPercentage: parseFloat(item.gstPercentage || 18)
          })
        });
      });
    
    try {
      await Promise.all(saveCatalogPromises);
    } catch (err) {
      console.error("Failed to save some products to catalog", err);
    }
    
    const enrichedItems = items.map(item => {
      const totals = calculateItemTotals(item);
      return { 
        description: item.description,
        hsnSacCode: item.hsnSacCode,
        qty: parseInt(item.qty),
        netPrice: parseFloat(item.netPrice),
        gstPercentage: parseFloat(item.gstPercentage),
        cgstAmount: totals.cgstAmount, 
        sgstAmount: totals.sgstAmount, 
        totalItemPrice: totals.totalItemPrice 
      };
    });

    const payload = {
      userId: user?.id || user?.Id,
      clientId: user?.clientId || user?.ClientId || 1,
      templateId: selectedTemplateId,
      invoicePrefix: invoicePrefix,
      paymentStatus: paymentStatus,
      templateColor: templateColor,
      billerCompany: biller.companyName,
      billerAddress: biller.address,
      billerContact: biller.contact,
      billerGSTIN: biller.gstin,
      billToCompany: billTo.company,
      billToName: billTo.name,
      billToAddress: billTo.address,
      billToGSTIN: billTo.gstin,
      subTotal, totalCGST, totalSGST, grandTotal,
      items: enrichedItems,
      payment: { ...payment, amount: payment.amount > 0 ? payment.amount : grandTotal }
    };

    try {
      const res = await fetch('/api/invoice/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.Success || data.success) {
        // Fetch full invoice detail to show in preview
        const detailRes = await fetch(`/api/invoice/${data.InvoiceId || data.invoiceId}`);
        const detailData = await detailRes.json();
        setGeneratedInvoice(detailData);
      } else {
        alert("Error generating invoice.");
      }
    } catch (err) {
      alert("API Error");
    }
  };

  if (generatedInvoice) {
    return (
      <div>
        <div className="no-print" style={{ padding: '1rem', background: '#D1FAE5', color: '#065F46', borderRadius: '8px', marginBottom: '2rem', display: 'flex', alignItems: 'center' }}>
           <CheckCircle style={{ marginRight: '0.5rem' }} /> Successfully Generated Invoice: {generatedInvoice.invoice.invoiceNo || generatedInvoice.invoice.InvoiceNo}
        </div>
        <InvoicePreview invoiceData={generatedInvoice} templateComponent={selectedTemplateComponent} />
        <div className="no-print" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <button onClick={() => { setGeneratedInvoice(null); onCreated(); }} className="btn-outline">
            Go to Invoice History
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="animate-fade-in">
      
      {/* Settings Section */}
      <div className="form-section" style={{ marginBottom: '2rem' }}>
        <h3 className="section-title">Invoice Settings</h3>
        <div className="grid-2-col">
          {(user?.role || user?.Role) === 'SuperAdmin' ? (
          <div>
            <label className="input-label">Select Invoice Template (Admin Only)</label>
            <select className="premium-input" value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}>
               {templates?.map(t => (
                 <option key={t.Id || t.id} value={t.Id || t.id}>{t.Name || t.name}</option>
               ))}
            </select>
          </div>
          ) : ( <div /> )}
          <div>
            <label className="input-label">Custom Invoice Prefix</label>
            <input type="text" className="premium-input" value={invoicePrefix} onChange={e => setInvoicePrefix(e.target.value)} placeholder="e.g. #INT-IV" />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Generated as: {invoicePrefix}26-041300001</div>
          </div>
          
          <div style={{ gridColumn: 'span 2', marginTop: '1rem' }}>
            <label className="input-label" style={{ marginBottom: '0.75rem' }}>Dynamic Template Primary Color</label>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {PRESETS.map(preset => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setTemplateColor(preset.value)}
                  style={{
                    backgroundColor: preset.value,
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: templateColor === preset.value ? '3px solid #0f172a' : '1px solid rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    boxShadow: templateColor === preset.value ? `0 0 0 2px ${preset.value}50` : 'none',
                    transition: 'transform 0.2s',
                    transform: templateColor === preset.value ? 'scale(1.15)' : 'scale(1)'
                  }}
                  title={preset.name}
                />
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem', background: '#F8FAFC', padding: '0.25rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <input 
                  type="color" 
                  value={templateColor} 
                  onChange={e => setTemplateColor(e.target.value)} 
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', width: '28px', height: '28px' }} 
                />
                <input 
                  type="text" 
                  value={templateColor.toUpperCase()} 
                  onChange={e => setTemplateColor(e.target.value)} 
                  className="table-input" 
                  style={{ width: '90px', padding: '0.25rem 0.5rem', fontSize: '0.85rem', fontFamily: 'monospace', textTransform: 'uppercase' }} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-section" style={{ marginBottom: '2rem' }}>
        <h3 className="section-title">Biller Information (Your Details)</h3>
        <div className="grid-2-col">
          <div><label className="input-label">Company Name</label><input required type="text" className="premium-input" value={biller.companyName} onChange={e => setBiller({...biller, companyName: e.target.value})} /></div>
          <div><label className="input-label">GSTIN</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input required type="text" className="premium-input" value={biller.gstin} onChange={e => setBiller({...biller, gstin: e.target.value})} />
              <button type="button" onClick={() => handleGSTVerify('biller')} className="btn-outline" style={{ padding: '0.5rem', width: 'auto' }} disabled={isVerifying.biller}>
                {isVerifying.biller ? '...' : <Search size={18} />}
              </button>
            </div>
          </div>
          <div style={{ gridColumn: 'span 2' }}><label className="input-label">Address</label><textarea required className="premium-input" rows="2" value={biller.address} onChange={e => setBiller({...biller, address: e.target.value})}></textarea></div>
          <div><label className="input-label">Contact Number</label><input required type="text" className="premium-input" value={biller.contact} onChange={e => setBiller({...biller, contact: e.target.value})} /></div>
        </div>
      </div>

      <div className="form-section" style={{ marginBottom: '2rem' }}>
        <h3 className="section-title">Bill To (Client Details)</h3>
        <div className="grid-2-col">
          <div><label className="input-label">Company Name</label><input required type="text" className="premium-input" value={billTo.company} onChange={e => setBillTo({...billTo, company: e.target.value})} /></div>
          <div><label className="input-label">Contact Person</label><input required type="text" className="premium-input" value={billTo.name} onChange={e => setBillTo({...billTo, name: e.target.value})} /></div>
          <div style={{ gridColumn: 'span 2' }}><label className="input-label">Client Address</label><textarea required className="premium-input" rows="2" value={billTo.address} onChange={e => setBillTo({...billTo, address: e.target.value})}></textarea></div>
          <div><label className="input-label">Client GSTIN</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input type="text" className="premium-input" value={billTo.gstin} onChange={e => setBillTo({...billTo, gstin: e.target.value.toUpperCase()})} placeholder="Enter GSTIN to Autofill" />
              <button type="button" onClick={() => handleGSTVerify('billTo')} className="btn-outline" style={{ padding: '0.5rem', width: 'auto' }} disabled={isVerifying.billTo}>
                {isVerifying.billTo ? '...' : <Search size={18} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="form-section" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem', gap: '1rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Invoice Items</h3>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <select onChange={handleQuickAdd} className="premium-input" style={{ width: '250px', padding: '0.5rem 0.75rem', fontSize: '0.85rem' }} defaultValue="">
              <option value="" disabled>⚡ Quick Add Service/Product</option>
              {STANDARD_PRODUCTS.map((prod, idx) => (
                <option key={idx} value={idx}>{prod.description} (₹{prod.netPrice})</option>
              ))}
            </select>
            <button type="button" onClick={addItem} className="btn-outline" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', width: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Plus size={16} /> Add Item</button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="premium-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>S.No</th><th>Description</th><th style={{ width: '100px' }}>HSN/SAC</th><th style={{ width: '70px' }}>Qty</th><th style={{ width: '110px' }}>Net Price</th><th style={{ width: '85px' }}>GST %</th><th style={{ width: '120px' }}>Total</th><th style={{ width: '65px', textAlign: 'center' }}>Save DB</th><th style={{ width: '45px' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const totals = calculateItemTotals(item);
                return (
                  <tr key={index}>
                    <td style={{ color: 'var(--text-muted)' }}>{index + 1}</td>
                    <td><input required type="text" className="table-input" value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} /></td>
                    <td><input type="text" className="table-input" value={item.hsnSacCode} onChange={e => handleItemChange(index, 'hsnSacCode', e.target.value)} /></td>
                    <td><input required type="number" min="1" className="table-input" value={item.qty} onChange={e => handleItemChange(index, 'qty', e.target.value)} /></td>
                    <td><input required type="number" min="0" step="0.01" className="table-input" value={item.netPrice} onChange={e => handleItemChange(index, 'netPrice', e.target.value)} /></td>
                    <td><input required type="number" min="0" max="100" className="table-input" value={item.gstPercentage} onChange={e => handleItemChange(index, 'gstPercentage', e.target.value)} /></td>
                    <td style={{ fontWeight: '500' }}>₹{totals.totalItemPrice.toFixed(2)}</td>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <input 
                        type="checkbox" 
                        checked={item.saveToCatalog || false} 
                        onChange={e => handleItemChange(index, 'saveToCatalog', e.target.checked)} 
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        title="Save to product catalog database upon saving invoice"
                      />
                    </td>
                    <td><button type="button" onClick={() => removeItem(index)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '0.5rem' }}><Trash2 size={18} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
          <div style={{ width: '300px', background: '#F8FAFC', padding: '1.5rem', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}><span style={{ color: 'var(--text-muted)' }}>Sub Total:</span><span style={{ fontWeight: '600' }}>₹{subTotal.toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}><span style={{ color: 'var(--text-muted)' }}>Total CGST:</span><span>₹{totalCGST.toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}><span style={{ color: 'var(--text-muted)' }}>Total SGST:</span><span>₹{totalSGST.toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1rem', borderTop: '2px solid var(--border-color)', fontSize: '1.25rem' }}><span style={{ fontWeight: '700', color: 'var(--text-dark)' }}>Total:</span><span style={{ fontWeight: '700', color: 'var(--primary)' }}>₹{grandTotal.toFixed(2)}</span></div>
          </div>
        </div>
      </div>

      <div className="form-section" style={{ marginBottom: '2rem' }}>
        <h3 className="section-title">Payment Details</h3>
        <div className="grid-2-col">
          <div><label className="input-label">Transaction Description</label><input required type="text" className="premium-input" value={payment.transactionDesc} onChange={e => setPayment({...payment, transactionDesc: e.target.value})} /></div>
          <div><label className="input-label">Mode of Payment</label>
            <select className="premium-input" value={payment.modeOfPayment} onChange={e => setPayment({...payment, modeOfPayment: e.target.value})}>
                          <option value="Bank Transfer">Bank Transfer</option><option value="UPI">UPI</option><option value="Cash">Cash</option><option value="Cheque">Cheque</option><option value="Debit Card">Debit Card</option><option value="Debit Card">Credit Card</option>
            </select>
          </div>
          <div><label className="input-label">Transaction Date</label><input required type="date" className="premium-input" value={payment.transactionDate} onChange={e => setPayment({...payment, transactionDate: e.target.value})} /></div>
          <div><label className="input-label">Transaction ID</label><input type="text" className="premium-input" placeholder="Optional" value={payment.transactionId} onChange={e => setPayment({...payment, transactionId: e.target.value})} /></div>
          <div><label className="input-label">Amount Paid / Due</label><input required type="number" step="0.01" className="premium-input" value={payment.amount || grandTotal} onChange={e => setPayment({...payment, amount: parseFloat(e.target.value)})} /></div>
          <div><label className="input-label">Invoice Status</label>
            <select className="premium-input" style={{ fontWeight: '600', color: paymentStatus === 'Paid' ? 'var(--secondary)' : 'var(--danger)' }} value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)}>
               <option value="Paid">Paid</option>
               <option value="Unpaid">Unpaid</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '1rem 3rem' }}><FileText size={20} style={{ marginRight: '0.5rem' }} /> Generate Invoice</button>
      </div>
    </form>
  );
};

export default InvoiceForm;
